import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Converter } from 'opencc-js';

// Parses LRC string into an array of { time, text } objects
const parseLrc = (lrcString) => {
    if (!lrcString) return [];
    const lines = lrcString.split('\n');
    const lyrics = [];
    const timeRegex = /\[(\d{2}):(\d{2})\.(\d{2,3})\]/; // Corrected regex

    for (const line of lines) {
        const match = timeRegex.exec(line);
        if (match) {
            const minutes = parseInt(match[1], 10);
            const seconds = parseInt(match[2], 10);
            const milliseconds = parseInt(match[3], 10);
            const time = minutes * 60 + seconds + milliseconds / 1000;
            const text = line.replace(timeRegex, '').trim();
            if (text) {
                lyrics.push({ time, text });
            }
        }
    }
    return lyrics;
};

// Converters - create them once
const s2t = Converter({ from: 'cn', to: 'tw' });
const t2s = Converter({ from: 'tw', to: 'cn' });

function TrackDetail({ track }) {
    const [parsedLyrics, setParsedLyrics] = useState([]);
    const [currentLyricIndex, setCurrentLyricIndex] = useState(-1);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPackaging, setIsPackaging] = useState(false);
    const [lyricDisplayMode, setLyricDisplayMode] = useState('original'); // 'original', 't', 's'
    const audioRef = useRef(null);
    const lyricContainerRef = useRef(null);

    const displayedLyrics = useMemo(() => {
        switch (lyricDisplayMode) {
            case 't':
                return s2t(track.lyrics);
            case 's':
                return t2s(track.lyrics);
            default:
                return track.lyrics;
        }
    }, [track.lyrics, lyricDisplayMode]);

    useEffect(() => {
        setParsedLyrics(parseLrc(displayedLyrics));
        setCurrentLyricIndex(-1);
    }, [displayedLyrics]);

    useEffect(() => {
        // When the track changes, reset the lyric mode to original
        setLyricDisplayMode('original');
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    }, [track]);

    useEffect(() => {
        if (currentLyricIndex > -1) {
            const activeLyricElement = lyricContainerRef.current?.children[currentLyricIndex];
            if (activeLyricElement) {
                activeLyricElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }, [currentLyricIndex]);

    const handleTimeUpdate = () => {
        if (!audioRef.current || parsedLyrics.length === 0) return;
        const currentTime = audioRef.current.currentTime;
        let lyricIndex = -1;
        for (let i = parsedLyrics.length - 1; i >= 0; i--) {
            if (parsedLyrics[i].time <= currentTime) {
                lyricIndex = i;
                break;
            }
        }
        if (lyricIndex !== currentLyricIndex) {
            setCurrentLyricIndex(lyricIndex);
        }
    };

    const handleLyricClick = (time) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
        }
    };

    const handleForceDownload = async (type = 'raw') => { // 'raw', 'packaged', 'experimental'
        if (!track.playUrl) return;

        let setLoading;
        let endpoint;
        let downloadFileName;
        let body;

        if (type === 'raw') {
            setLoading = setIsDownloading;
            endpoint = track.playUrl; // Direct URL for raw download
            downloadFileName = `${track.ar.map(a => a.name).join(', ')} - ${track.name}.flac`;
        } else if (type === 'packaged') {
            setLoading = setIsPackaging;
            endpoint = `${process.env.REACT_APP_API_URL}/api/download/packaged`;
            downloadFileName = `${track.ar.map(a => a.name).join(', ')} - ${track.name}_stable.zip`;
            body = JSON.stringify({ tracks: [track] });
        }

        setLoading(true);
        try {
            const fetchOptions = {
                method: (type === 'raw') ? 'GET' : 'POST', // Direct URL needs GET
            };
            if (body) {
                fetchOptions.headers = { 'Content-Type': 'application/json' };
                fetchOptions.body = body;
            }
            
            const response = await fetch(endpoint, fetchOptions);

            if (!response.ok) {
                throw new Error(`Server error: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = downloadFileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Download error:', error);
            alert('下載失敗，請檢查網路連線或稍後再試。');
        } finally {
            setLoading(false);
        }
    };

    if (!track) return null;

    return (
        <div className="card p-4 shadow mb-4">
            <div className="row">
                <div className="col-md-4 text-center">
                    {track.al && track.al.picUrl && (
                        <img src={track.al.picUrl} alt="Album Cover" className="img-fluid rounded shadow-sm mb-3" />
                    )}
                </div>
                <div className="col-md-8">
                    <h3 className="mb-2">{track.name}</h3>
                    <h4 className="h5 text-muted">藝人: {track.ar.map(a => a.name).join(', ')}</h4>
                    <h4 className="h5 text-muted mb-3">專輯: {track.al.name}</h4>
                    
                    <div className="d-flex justify-content-between align-items-center">
                        <h5>歌詞</h5>
                        <div className="btn-group btn-group-sm" role="group">
                            <button type="button" className={`btn ${lyricDisplayMode === 'original' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setLyricDisplayMode('original')}>原文</button>
                            <button type="button" className={`btn ${lyricDisplayMode === 't' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setLyricDisplayMode('t')}>繁體</button>
                            <button type="button" className={`btn ${lyricDisplayMode === 's' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setLyricDisplayMode('s')}>简体</button>
                        </div>
                    </div>
                    <div ref={lyricContainerRef} className="card card-body bg-body mt-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        {parsedLyrics.length > 0 ? (
                            parsedLyrics.map((line, index) => (
                                <p key={index} className={`mb-1 transition-all duration-300 ${index === currentLyricIndex ? 'fw-bold text-primary fs-5' : 'text-muted'}`} style={{ cursor: 'pointer' }} onClick={() => handleLyricClick(line.time)}>
                                    {line.text}
                                </p>
                            ))
                        ) : (
                            <p>{track.lyrics}</p>
                        )}
                    </div>
                </div>
            </div>

            {track.playUrl ? (
                <div className="mt-4">
                    <audio controls src={track.playUrl} ref={audioRef} onTimeUpdate={handleTimeUpdate} className="w-100" />
                    <div className="d-grid gap-2 d-sm-flex justify-content-sm-center mt-2">
                        <button onClick={() => handleForceDownload('raw')} disabled={isDownloading} className="btn btn-info flex-fill">
                            {isDownloading ? '下載中...' : '下載單曲 (.flac)'}
                        </button>
                        <button onClick={() => handleForceDownload('packaged')} disabled={isPackaging} className="btn btn-success flex-fill">
                            {isPackaging ? '打包中...' : '打包單曲 (ZIP)'}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="mt-4 text-center text-muted">
                    <p>此歌曲無播放連結或受版權保護。</p>
                </div>
            )}
        </div>
    );
}

export default TrackDetail;